import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import { getAllRatesFromRateGroups } from './rate-groups-service';
import { RateTag } from './rate-tag';
import { useRateViewerError } from './use-rate-viewer-error';
import { useRateViewerLabelProviders } from './use-rate-viewer-label-providers';
import { useRateViewerProgress } from './use-rate-viewer-progress';
import { useRateViewerRates } from './use-rate-viewer-rates';
import { RateViewerView } from './views/rate-viewer-view';
import { EXPERIMENT_VIEWED } from '../../constants/analytics-events';
import { RATE_BROWSER } from '../../constants/fulfillment-plan-ids';
import { useRateBrowserDefaultView } from '../../feature-flags/hooks/use-rate-browser-default-view';
import { useDeprecatedLocalStorage, generateUserSpecificPersistentKey, RATE_VIEWER_SELECTED_VIEW } from '../../hooks/use-local-storage';
import { useOnComponentMount } from '../../hooks/use-on-component-mount';
import { ChildrenPropType } from '../../prop-types/children';
import { RateBrowserShipmentConfigurationPropType } from '../../prop-types/rate-browser-shipment-configuration';
import { Segment } from '../../services/analytics-services/segment';
import Global from '../../store/global-selectors';
import { selectRateViewerErrors } from '../../store/rate-viewer/rate-viewer.selectors';
import { selectRate, selectSellerProvider } from '../../store/rate-viewer/rate-viewer.actions';
import { useTrackRateBrowser } from './use-track-rate-browser';
import { trackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { Workflows } from '../../tracking/tracking-constants';

export const RATE_BROWSER_DEFAULT_VIEW_EXPERIMENT_NAME = 'Rate Browser Default View';

const initialValue = {
  allRatesErrorMessage: [''],
  configureLabel: noop,
  dismissErrorMessage: noop,
  filteredRateGroups: [],
  filteredSellerLabelProviders: [],
  onToggleErrorMessageExpander: noop,
  rateProvidersLoadedCount: 0,
  rateProvidersRequestedCount: 0,
  selectedRate: {},
  selectedView: RateViewerView.AllRates,
  shouldShowAllRatesError: false,
  shouldShowLoadingIndicator: false,
  shouldShowProgress: false,
  updateSelectedRate: noop,
  updateSelectedView: noop,
  validationErrors: [],
};

const RateViewerContext = createContext(initialValue);

const getRecommendedAndCheapestRates = (rateGroups) => {
  const rates = getAllRatesFromRateGroups(rateGroups);
  const recommendedRate = rates.find((rate) => rate.tag === RateTag.Recommended);
  const cheapestUntaggedRate = rates.find((rate) => !rate.tag);

  const cheapestRate =
    recommendedRate?.rate?.totalCost?.value < cheapestUntaggedRate?.rate?.totalCost?.value ? recommendedRate : cheapestUntaggedRate;

  return {
    recommendedRate,
    cheapestRate,
  };
};

export const useRateViewerContext = () => useContext(RateViewerContext);

export const RateViewerContextProvider = ({
  onConfigureLabel,
  onBrowseRates,
  shipmentConfiguration,
  hideLocalDelivery = false,
  rateViewerKey,
  children,
}) => {
  const trackRateBrowser = useTrackRateBrowser();
  const [selectedRate, setSelectedRate] = useState(undefined);
  const [hasTrackedConfigureLabel, setHasTrackedConfigureLabel] = useState(false);
  const [hasTrackedAllRatesReturned, setHasTrackedAllRatesReturned] = useState(false);

  const sellerId = useSelector(Global.selectSellerSellerId);
  const userId = useSelector(Global.selectUserCurrentUserId);

  const defaultView = useRateBrowserDefaultView();
  const [selectedView, setSelectedView] = useDeprecatedLocalStorage(
    generateUserSpecificPersistentKey(RATE_VIEWER_SELECTED_VIEW, userId),
    defaultView || RateViewerView.AllRates,
  );
  const filteredSellerLabelProviders = useRateViewerLabelProviders(hideLocalDelivery);

  const filteredRateGroups = useRateViewerRates(rateViewerKey, selectedView, filteredSellerLabelProviders);

  const validationErrors = useSelector((state) => selectRateViewerErrors(state, { rateViewerKey }));

  const { shouldShowProgress, shouldShowLoadingIndicator, rateProvidersLoadedCount, rateProvidersRequestedCount } = useRateViewerProgress(
    rateViewerKey,
    filteredRateGroups,
    selectedView,
  );

  const { allRatesErrorMessage, shouldShowAllRatesError, dismissErrorMessage, onToggleErrorMessageExpander, resetErrorState } =
    useRateViewerError({
      filteredRateGroups,
      isLoadingRates: shouldShowLoadingIndicator,
      selectedView,
    });

  const dispatch = useDispatch();

  const configureLabel = useCallback(() => {
    const { sellerLabelProviderId } = selectedRate;

    dispatch(selectSellerProvider(rateViewerKey, sellerLabelProviderId));
    dispatch(selectRate(rateViewerKey, selectedRate));

    onConfigureLabel({
      sellerLabelProviderId,
      rate: selectedRate,
      fulfillmentPlanId: RATE_BROWSER,
    });

    if (hasTrackedConfigureLabel) {
      return;
    }

    const { recommendedRate, cheapestRate } = getRecommendedAndCheapestRates(filteredRateGroups);

    trackRateBrowser(WorkflowSteps.RATE_BROWSER_CONFIGURE_LABEL, {
      cheapestRate,
      recommendedRate,
      selectedRate,
      selectedView,
      shipmentConfiguration,
    });
    setHasTrackedConfigureLabel(true);
  }, [
    dispatch,
    filteredRateGroups,
    hasTrackedConfigureLabel,
    onConfigureLabel,
    rateViewerKey,
    selectedRate,
    selectedView,
    shipmentConfiguration,
    trackRateBrowser,
  ]);

  const updateSelectedView = useCallback(
    (newView) => {
      setSelectedView(newView);
      trackRateBrowser(WorkflowSteps.RATE_BROWSER_CHANGE_VIEW, {
        view: newView,
      });
      setHasTrackedConfigureLabel(false);
    },
    [setSelectedView, trackRateBrowser],
  );

  const browseRates = useCallback(
    (request) => {
      onBrowseRates(request);
      setHasTrackedAllRatesReturned(false);
      setHasTrackedConfigureLabel(false);
      resetErrorState();
    },
    [onBrowseRates, resetErrorState],
  );

  const updateSelectedRate = useCallback((newRate) => {
    setSelectedRate(newRate);
    setHasTrackedConfigureLabel(false);
  }, []);

  const haveAllRatesReturned = rateProvidersRequestedCount > 0 && rateProvidersLoadedCount === rateProvidersRequestedCount;
  useEffect(() => {
    if (!haveAllRatesReturned || hasTrackedAllRatesReturned) {
      return;
    }

    const { recommendedRate, cheapestRate } = getRecommendedAndCheapestRates(filteredRateGroups);

    trackWorkflow(Workflows.RATE_BROWSER_FLOW, WorkflowSteps.RATE_BROWSER_ALL_RATES_RETURNED, {
      cheapestRate,
      recommendedRate,
      shipmentConfiguration,
    });
    setHasTrackedAllRatesReturned(true);
  }, [filteredRateGroups, hasTrackedAllRatesReturned, haveAllRatesReturned, shipmentConfiguration]);

  // reset "configure label" tracking when any new rates come in
  useEffect(() => {
    setHasTrackedConfigureLabel(false);
  }, [filteredRateGroups.length]);

  useOnComponentMount(() => {
    Segment.trackEvent(EXPERIMENT_VIEWED, {
      experimentName: RATE_BROWSER_DEFAULT_VIEW_EXPERIMENT_NAME,
      variationName: defaultView,
      sellerId,
    });
  });

  const value = useMemo(() => {
    return {
      allRatesErrorMessage,
      browseRates,
      configureLabel,
      dismissErrorMessage,
      filteredRateGroups,
      filteredSellerLabelProviders,
      onToggleErrorMessageExpander,
      rateProvidersLoadedCount,
      rateProvidersRequestedCount,
      rateViewerKey,
      selectedRate,
      selectedView,
      shouldShowAllRatesError,
      shouldShowLoadingIndicator,
      shouldShowProgress,
      updateSelectedRate,
      updateSelectedView,
      validationErrors,
    };
  }, [
    allRatesErrorMessage,
    browseRates,
    configureLabel,
    dismissErrorMessage,
    filteredRateGroups,
    filteredSellerLabelProviders,
    rateProvidersLoadedCount,
    rateProvidersRequestedCount,
    rateViewerKey,
    selectedRate,
    selectedView,
    shouldShowAllRatesError,
    shouldShowLoadingIndicator,
    shouldShowProgress,
    onToggleErrorMessageExpander,
    updateSelectedRate,
    updateSelectedView,
    validationErrors,
  ]);

  return <RateViewerContext.Provider value={value}>{children}</RateViewerContext.Provider>;
};

RateViewerContextProvider.propTypes = {
  onBrowseRates: PropTypes.func.isRequired,
  onConfigureLabel: PropTypes.func.isRequired,
  shipmentConfiguration: RateBrowserShipmentConfigurationPropType.isRequired,
  hideLocalDelivery: PropTypes.bool,
  rateViewerKey: PropTypes.string.isRequired,
  children: ChildrenPropType,
};
